import React, { useCallback, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";

// region Libraries
import Yup from "@libraries/yup";
import { TextField } from "@libraries/mui";
import { Button, useToast } from "@npm-telluria-tecnologia/telluria-ui";
// endregion Libraries
// region Store
import {
  resendEmailConfirmation, TUserEmailMutations
} from "@graphql/groups/user/mutation.graphql";
import { UserMutationsUserEmailArgs } from "@graphql/helpers/types.graphql";
import utils from "@helpers/utils";
// endregion Store
// region Language
import useTranslation from "@languages/useTranslation";
import { ResendConfirmationEmailMessages } from "@languages/interfaces";
// endregion Language
// region Atoms
import ExternalSvg from "@atoms/ExternalSvg";
// endregion Atoms
// region Molecules
import PageHeader from "@molecules/PageHeader";
// endregion Molecules
// region Styles
import * as Styled from "./styles";
// endregion Styles

interface IResendEmailConfirmationFormData {
  email: string;
}

const FormResendEmailConfirmation: React.FC = () => {

  const { t } = useTranslation();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [MutationResendEmailConfirmation] = useMutation<TUserEmailMutations, UserMutationsUserEmailArgs>(
    resendEmailConfirmation()
  );

  // Define the validation types
  const resendEmailConfirmationSchema = Yup.object()
    .shape({
      email: Yup.string()
        .trim()
        .required(t(ResendConfirmationEmailMessages.resendConfirmationEmailRequiredEmail))
        .email(t(ResendConfirmationEmailMessages.resendConfirmationEmailInvalidEmail)),
      fullName: Yup.string()
    });

  // Form control
  const {
    control,
    handleSubmit,
    formState: { errors: formErrors }
  } = useForm<IResendEmailConfirmationFormData>({
    resolver: yupResolver(resendEmailConfirmationSchema),
    reValidateMode: "onChange",
    mode: "onBlur"
  });

  /**
   * Send mail to user
   * @param data User data to send
   */
  const sendConfirmationEmail = useCallback(async (data: IResendEmailConfirmationFormData) => {

    const { email } = data;
    const mutation = await MutationResendEmailConfirmation({
      variables: {
        user: {
          email
        }
      }
    });

    return mutation.data?.user.resendEmailConfirmation;
  }, []);

  /**
   * Handle submit form
   * @param data User data from form
   */
  const handleResendConfirmationEmailForm = useCallback(async (data) => {

    setIsLoading(true);

    try {

      const response = await sendConfirmationEmail(data);
      const toastStatus = utils.getToastStatusType(response?.status);

      if (response?.status === "SUCCESS") {
        addToast({
          type: toastStatus,
          title: response!.message
        });

        setIsLoading(false);

        navigate("/login");
      }

    } catch (error) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: error.message
      });
      setIsLoading(false);
    }

  }, [addToast, navigate, sendConfirmationEmail]);

  // Handle form controllers
  const handleSubmitForm: SubmitHandler<IResendEmailConfirmationFormData> = (
    formData: IResendEmailConfirmationFormData
  ) => handleResendConfirmationEmailForm(formData);

  return (
    <Styled.ResendEmailConfirmationFormContainer>
      <Styled.ResendEmailConfirmationFormLogo>
        <ExternalSvg svgName="LogotipoMinifiedColor" />
      </Styled.ResendEmailConfirmationFormLogo>
      <PageHeader url="/login" title={t(ResendConfirmationEmailMessages.resendConfirmationEmailTitlePage)} />
      <form onSubmit={handleSubmit(handleSubmitForm)}>
        <Controller // Email
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              autoComplete="username"
              label={t(ResendConfirmationEmailMessages.resendConfirmationEmailLabel)}
              type="email"
              variant="outlined"
              fullWidth
              size="small"
              error={!!formErrors.email}
              helperText={formErrors.email && formErrors.email.message}
              className="default-field"
            />
          )}
        />
        <Button
          variant="contained"
          size="small"
          type="submit"
          text={t(ResendConfirmationEmailMessages.resendConfirmationEmailButton)}
          loading={isLoading}
        />
      </form>
    </Styled.ResendEmailConfirmationFormContainer>
  );
};

export default FormResendEmailConfirmation;
